import { useCallback } from 'react';
import { useFeatureFlagsContext } from '../ui/FeatureFlagsContext/FeatureFlagsContext';

export enum Features {
  eventPostbacksReview = 'eventPostbacksReview',
  lifetimeSelectTwoYearsExtension = 'lifetimeSelectTwoYearsExtension',
  lifetimeSelectThreeYearsExtension = 'lifetimeSelectThreeYearsExtension',
  productCatalog = 'productCatalog',
  multiOrganization = 'multiOrganization',
}

enum AccessEntity {
  user = 'user',
  company = 'company',
  organization = 'organization',
}
type AccessRecord =
  | {
    type: AccessEntity.company;
    postfix: string;
  }
  | { type: AccessEntity.user; email: string }
  | { type: AccessEntity.organization; organizationId: string };

const FEATURE_GROUPS: Record<Features, AccessRecord[]> = {
  [Features.eventPostbacksReview]: [
    { type: AccessEntity.company, postfix: 'campaignswell.com' },
  ],
  [Features.lifetimeSelectTwoYearsExtension]: [
    {
      type: AccessEntity.organization,
      organizationId: '4a6b0c93-ecda-46ea-bd58-92553abb73ae',
    },
    {
      type: AccessEntity.organization,
      organizationId: '7c2d76c6-b64a-4cc8-87e0-d5c303e77765',
    },
  ],
  [Features.lifetimeSelectThreeYearsExtension]: [
    {
      type: AccessEntity.organization,
      organizationId: 'dd59e7c3-7668-47d9-999c-7909cfabe234',
    },
  ],
  [Features.productCatalog]: [
    {
      type: AccessEntity.organization,
      organizationId: '76d26568-277a-4950-b075-125d17449f41',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'f5185652-604c-43ee-82bd-241d31f0ffeb',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'bd51ad58-8b38-4848-a719-eaea8252f6ab',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'bcc49cd1-e9b7-48f8-bf61-d85611db629c',
    },
    {
      type: AccessEntity.organization,
      organizationId: '7edc42f4-ba62-4290-a65f-1989774ac106',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'c9548ff9-1d5d-410f-afbe-f1fc5cb56b35',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'abdd4c37-b27d-4594-99dc-2d7e5e857a8d',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'dd5bd051-47ba-467d-9805-2d3dbcb589e9',
    },
    {
      type: AccessEntity.organization,
      organizationId: '242cc389-cf5c-4bd7-ac3c-bdd879f5f3c5',
    },
    {
      type: AccessEntity.organization,
      organizationId: '19d0c4f0-1e77-440e-b29d-e30e02c948db',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'ed2aff88-565d-42e0-9d64-25bf1231ee87',
    },
    {
      type: AccessEntity.organization,
      organizationId: '4a6b0c93-ecda-46ea-bd58-92553abb73ae',
    },
    {
      type: AccessEntity.organization,
      organizationId: '932d04c9-f8ca-4b86-acd5-2e6851936c12',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'cc642671-d665-4a95-84f8-4da7277ccb96',
    },
    {
      type: AccessEntity.organization,
      organizationId: '8acf1e60-f6be-4070-995b-9d6fa0f58489',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'fc7cf45b-3778-4361-b197-91e1ccd15862',
    },
    {
      type: AccessEntity.organization,
      organizationId: '5acac618-671d-41cb-91fb-d086123ef0e6',
    },
    {
      type: AccessEntity.organization,
      organizationId: '738cbd8b-56b1-472b-9666-d6f9aad9650a',
    },
    {
      type: AccessEntity.organization,
      organizationId: '454d5138-86c9-415d-b9dd-d69e0982beea',
    },
    {
      type: AccessEntity.organization,
      organizationId: '267423ef-d636-45eb-8cea-a910d0953dc1',
    },
    {
      type: AccessEntity.organization,
      organizationId: '4b90f654-a0e1-4a1f-8460-2a6d59f4371b',
    },
    {
      type: AccessEntity.organization,
      organizationId: '02738868-66e2-4108-985d-c6d1e4b2c69b',
    },
    {
      type: AccessEntity.organization,
      organizationId: '54118ea6-a267-4cc1-8146-17e8d2f084ca',
    },
    {
      type: AccessEntity.organization,
      organizationId: '72027eae-a5a6-4810-94f5-4fc795cc3334',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'f731a1d0-7e4e-463f-8d70-24dd117c5da6',
    },
    {
      type: AccessEntity.organization,
      organizationId: '4d37cffe-f2cd-43a3-b070-5f41ac16c718',
    },
    {
      type: AccessEntity.organization,
      organizationId: '00adc76e-96fc-4f40-a6a3-a0eabb14c0c2',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'e8eab7e1-5be2-47d9-a0a0-ffd494ba4a5c',
    },
    {
      type: AccessEntity.organization,
      organizationId: 'de17f195-7ce4-4e80-91ed-bee3b5150bb5',
    },
  ],
  [Features.multiOrganization]: [
    {
      type: AccessEntity.company,
      postfix: 'campaignswell.com'
    },
  ],
};

export const useFeatureFlags = () => {
  const { email, organizationId } = useFeatureFlagsContext();
  const isFeatureAvailable = useCallback(
    (feature: Features) => {
      const groups = FEATURE_GROUPS[feature];

      return (
        !!email &&
        groups.some((group) => {
          switch (group.type) {
            case AccessEntity.company:
              return email.endsWith(group.postfix);
            case AccessEntity.user:
              return email === group.email;
            case AccessEntity.organization:
              return organizationId === group.organizationId;
          }

          return false;
        })
      );
    },
    [email, organizationId],
  );

  return {
    isFeatureAvailable,
  };
};
